'use client';

import { c as _c } from "react/compiler-runtime";
import { NavArrowLeft, NavArrowRight } from '@byway/icons';
import { type CalendarDate, DateFormatter, getLocalTimeZone, today } from '@internationalized/date';
import { clsx } from 'clsx';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useRef } from 'react';
import { RangeCalendar as AriaRangeCalendar, CalendarCell, CalendarGrid, CalendarGridBody, CalendarGridHeader, CalendarHeaderCell, type DateValue, Heading, Text } from 'react-aria-components';
import { Button } from '~/components/Button';
import styles from './RangeCalendar.module.css';
import type { RangeCalendarProps } from './RangeCalendar.types';

/**
 * React Aria's RangeCalendar only fires onChange after two clicks. AutoComplete bridges
 * that gap for fixed-duration trips: when anchorDate transitions null → CalendarDate
 * (the user's first click), it immediately calls state.selectDate(anchor + fixedDuration - 1),
 * completing the range in a single click.
 *
 * useEffect is appropriate here because we're synchronising with React Aria's internal
 * state (an external system) — not React-state-to-React-state transformation.
 */
interface SelectableState {
  anchorDate: CalendarDate | null;
  selectDate: (date: CalendarDate) => void;
}
const AutoComplete = ({
  anchorDate,
  fixedDuration,
  state
}: {
  anchorDate: CalendarDate | null;
  fixedDuration: number;
  state: SelectableState;
}) => {
  const prevAnchor = useRef<CalendarDate | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  useEffect(() => {
    if (anchorDate && !prevAnchor.current) {
      stateRef.current.selectDate(anchorDate.add({
        days: fixedDuration - 1
      }));
    }
    prevAnchor.current = anchorDate;
  }, [anchorDate, fixedDuration]);
  return null;
};
const RangeCalendar = t0 => {
  const $ = _c(30);
  let errorMessage;
  let fixedDuration;
  let minDuration;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      errorMessage,
      minDuration,
      fixedDuration,
      layout: t1,
      monthsToShow: t2,
      variant: t3,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = errorMessage;
    $[2] = fixedDuration;
    $[3] = minDuration;
    $[4] = props;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    errorMessage = $[1];
    fixedDuration = $[2];
    minDuration = $[3];
    props = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
  }
  const layout = t1 === undefined ? "vertical" : t1;
  const monthsToShow = t2 === undefined ? 2 : t2;
  const variant = t3 === undefined ? "default" : t3;
  const t = useTranslations("aria");
  const locale = useLocale();
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    const now = today(getLocalTimeZone());
    t4 = now.add({
      days: 14
    });
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const earliestBookingDate = t4;
  const isHorizontal = layout === "horizontal";
  const isConnected = variant === "connected";
  const isDateUnavailable = _temp;
  let t5;
  if ($[9] !== locale) {
    t5 = new DateFormatter(locale, {
      month: "long",
      year: "numeric"
    });
    $[9] = locale;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const monthFormatter = t5;
  let t6;
  if ($[11] !== monthsToShow || $[12] !== props.visibleDuration) {
    t6 = props.visibleDuration ?? {
      months: monthsToShow
    };
    $[11] = monthsToShow;
    $[12] = props.visibleDuration;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const t7 = isConnected && styles.connected;
  let t8;
  if ($[14] !== t7) {
    t8 = clsx(styles["react-aria-RangeCalendar"], t7);
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== errorMessage || $[17] !== fixedDuration || $[18] !== isConnected || $[19] !== isHorizontal || $[20] !== minDuration || $[21] !== monthFormatter || $[22] !== monthsToShow || $[23] !== t) {
    t9 = renderProps => {
      const visibleRange = renderProps.state.visibleRange;
      const firstMonth = visibleRange.start;
      const secondMonth = firstMonth.add({
        months: 1
      });
      return <>{fixedDuration != null && <AutoComplete anchorDate={renderProps.state.anchorDate} fixedDuration={fixedDuration} state={renderProps.state as SelectableState} />}{isHorizontal ? <Heading className={styles.hiddenHeading} /> : <header className={styles.header}><Button variant="icon" ariaLabel={t("prev_month")} slot="previous"><NavArrowLeft decorative={true} size="large" color="currentColor" /></Button><Heading className={styles.hiddenHeading} /><Button variant="icon" ariaLabel={t("next_month")} slot="next"><NavArrowRight decorative={true} size="large" color="currentColor" /></Button></header>}<div className={clsx(styles.monthsContainer, {
          [styles.monthsContainerHorizontal]: isHorizontal
        })}><div className={clsx(styles.monthContainer, isHorizontal && styles.monthContainerHorizontal)}>{isHorizontal ? <div className={styles.horizontalMonthHeader}><Button variant="icon" ariaLabel={t("prev_month")} slot="previous"><NavArrowLeft decorative={true} size="large" color="currentColor" /></Button><h3 className={clsx(styles.monthHeader, styles.monthHeaderHorizontal)} data-test-id="range-calendar-month-header-1">{monthFormatter.format(firstMonth.toDate(getLocalTimeZone()))}</h3></div> : <h3 className={styles.monthHeader} data-test-id="range-calendar-month-header-1">{monthFormatter.format(firstMonth.toDate(getLocalTimeZone()))}</h3>}<CalendarGrid weekdayStyle={isConnected ? "narrow" : "short"} className={styles["react-aria-CalendarGrid"]}><CalendarGridHeader>{_temp2}</CalendarGridHeader><CalendarGridBody className={styles["react-aria-CalendarGridBody"]}>{date_0 => {
                  const isAnchorDate = renderProps.state.anchorDate ? date_0.compare(renderProps.state.anchorDate) === 0 : false;
                  const withinMinDuration = isDateUnavailable(date_0, minDuration, renderProps.state.anchorDate);
                  const dow = (date_0.toDate(getLocalTimeZone()).getDay() + 6) % 7;
                  return <CalendarCell date={date_0} className={t10 => {
                    const {
                      isUnavailable: isUnavailable_0
                    } = t10;
                    return clsx({
                      [styles["react-aria-CalendarCell"]]: true,
                      [styles.isRoundedLeft]: dow === 0 || date_0.day === 1,
                      [styles.isRoundedRight]: dow === 6 || date_0.day === date_0.calendar.getDaysInMonth(date_0),
                      [styles.isUnavailable]: isUnavailable_0 || withinMinDuration,
                      [styles.isAnchorDate]: isAnchorDate
                    });
                  }}>{isConnected ? _temp3 : undefined}</CalendarCell>;
                }}</CalendarGridBody></CalendarGrid></div>{monthsToShow === 2 && <div className={clsx(styles.monthContainer, isHorizontal && styles.monthContainerHorizontal)}>{isHorizontal ? <div className={styles.horizontalMonthHeader}><h3 className={clsx(styles.monthHeader, styles.monthHeaderHorizontal)} data-test-id="range-calendar-month-header-2">{monthFormatter.format(secondMonth.toDate(getLocalTimeZone()))}</h3><Button variant="icon" ariaLabel={t("next_month")} slot="next"><NavArrowRight decorative={true} size="large" color="currentColor" /></Button></div> : <h3 className={styles.monthHeader} data-test-id="range-calendar-month-header-2">{monthFormatter.format(secondMonth.toDate(getLocalTimeZone()))}</h3>}<CalendarGrid offset={{
              months: 1
            }} weekdayStyle={isConnected ? "narrow" : "short"} className={styles["react-aria-CalendarGrid"]}><CalendarGridHeader>{_temp4}</CalendarGridHeader><CalendarGridBody className={styles["react-aria-CalendarGridBody"]}>{date_1 => {
                  const isAnchorDate_0 = renderProps.state.anchorDate ? date_1.compare(renderProps.state.anchorDate) === 0 : false;
                  const withinMinDuration_0 = isDateUnavailable(date_1, minDuration, renderProps.state.anchorDate);
                  const dow_0 = (date_1.toDate(getLocalTimeZone()).getDay() + 6) % 7;
                  return <CalendarCell date={date_1} aria-disabled={isAnchorDate_0 || withinMinDuration_0} className={t11 => {
                    const {
                      isUnavailable: isUnavailable_1
                    } = t11;
                    return clsx({
                      [styles["react-aria-CalendarCell"]]: true,
                      [styles.isRoundedLeft]: dow_0 === 0 || date_1.day === 1,
                      [styles.isRoundedRight]: dow_0 === 6 || date_1.day === date_1.calendar.getDaysInMonth(date_1),
                      [styles.isUnavailable]: isUnavailable_1 || withinMinDuration_0,
                      [styles.isAnchorDate]: isAnchorDate_0
                    });
                  }}>{isConnected ? _temp5 : undefined}</CalendarCell>;
                }}</CalendarGridBody></CalendarGrid></div>}</div>{errorMessage && <Text className={styles["react-aria-text"]} slot="errorMessage">{errorMessage}</Text>}</>;
    };
    $[16] = errorMessage;
    $[17] = fixedDuration;
    $[18] = isConnected;
    $[19] = isHorizontal;
    $[20] = minDuration;
    $[21] = monthFormatter;
    $[22] = monthsToShow;
    $[23] = t;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== props || $[26] !== t6 || $[27] !== t8 || $[28] !== t9) {
    t10 = <AriaRangeCalendar data-test-id="RangeCalendar" visibleDuration={t6} pageBehavior="single" className={t8} minValue={earliestBookingDate} firstDayOfWeek="mon" {...props}>{t9}</AriaRangeCalendar>;
    $[25] = props;
    $[26] = t6;
    $[27] = t8;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  return t10;
};
export { RangeCalendar };
function _temp(date, minDuration_0, anchorDate) {
  if (!anchorDate || !minDuration_0) {
    return false;
  }
  const invalidRanges = [[anchorDate, anchorDate.add({
    days: minDuration_0
  })], [anchorDate.subtract({
    days: minDuration_0
  }), anchorDate]];
  const isUnavailable = invalidRanges.some(range => date.compare(range[0]) > 0 && date.compare(range[1]) < 0);
  return isUnavailable;
}
function _temp2(day) {
  return <CalendarHeaderCell className={styles["react-aria-CalendarHeaderCell"]}>{day}</CalendarHeaderCell>;
}
function _temp3(t0) {
  const {
    defaultChildren
  } = t0;
  return <span className={styles.cellInner}>{defaultChildren}</span>;
}
function _temp4(day_0) {
  return <CalendarHeaderCell className={styles["react-aria-CalendarHeaderCell"]}>{day_0}</CalendarHeaderCell>;
}
function _temp5(t0) {
  const {
    defaultChildren: defaultChildren_0
  } = t0;
  return <span className={styles.cellInner}>{defaultChildren_0}</span>;
}